/* @jsx h */
import { defineComponent, ref, computed, onMounted, h } from "vue";
import { LemmonModal } from "@app/vue/components/UIComponents";
import { api } from "@app/data/api";

import { formatPriceInLocale } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "@app/vue/providers/Auth";

const PAYMENT_METHODS = ["cash", "card", "manual"];

export type AddClosingReportModalProps = {
  onClose: Function;
  isSavingReport: boolean;
  onSubmit: (attrs: {
    starting_balances: Record<string, number>;
    closing_balances: Record<string, number>;
    differences: Record<string, number>;
    notes: string;
    shift_id: string;
  }) => Promise<void>;
};

export const AddClosingReportModal =
  defineComponent<AddClosingReportModalProps>({
    name: "AddClosingReportModal",
    props: ["onClose", "onSubmit", "isSavingReport"],
    setup(props) {
      const auth = useAuth();
      const { t } = useLocalization();
      const startingBalances = ref<Record<string, number>>({
        cash: 0,
        card: 0,
        manual: 0,
      });
      const shiftId = ref<string>();
      const userHasOpenTables = ref(false);

      const closingBalances = ref<Record<string, number>>({
        cash: 0,
        card: 0,
        manual: 0,
      });

      const recordedSales = ref<Record<string, number>>({
        cash: 0,
        card: 0,
        manual: 0,
        total: 0,
      });

      const recordedTips = ref<Record<string, number>>({
        cash: 0,
        card: 0,
        manual: 0,
        total: 0,
      });

      const notes = ref("");
      const loading = ref(true);
      const error = ref("");

      const differences = computed(() => {
        const diffs: Record<string, number> = {};
        PAYMENT_METHODS.forEach((method) => {
          const recorded =
            recordedSales.value[method] + recordedTips.value[method];
          const actual =
            closingBalances.value[method] -
            (method === "cash" ? startingBalances.value[method] : 0);
          diffs[method] = Number((actual - recorded).toFixed(2));
        });
        return diffs;
      });

      const getDifferenceClass = (value: number) => {
        if (value === 0) return "text-success";
        if (Math.abs(value) <= 1) return "text-warning";
        return "text-error";
      };
      const getDifferenceIcon = (value: number) => {
        if (value === 0) return "check";
        if (value > 0) return "trending_up";
        return "trending_down";
      };

      const fetchData = async () => {
        try {
          const activeShift = await api.getActiveShift();
          if (!activeShift) {
            alert("Cannot file closing report outside of working hours");
            return;
          }
          shiftId.value = activeShift.id;

          // Check for open tables with orders matching the active shift
          const tablesMeta = await api.getTablesMeta();
          const hasOpenTables = tablesMeta.some(
            (tableStatus) =>
              tableStatus.table.is_busy === 1 &&
              tableStatus.orders.some(
                (order) => order.shift_id === activeShift.id,
              ),
          );
          userHasOpenTables.value = hasOpenTables;

          const [salesResponse, tipsData] = await Promise.all([
            api.getPaymentsSummary({
              p_shift_id: activeShift.id,
            }),
            api.getTips({
              p_shift_id: activeShift.id,
            }),
          ]);

          recordedSales.value = {
            cash: salesResponse.cash_amount || 0,
            card:
              (salesResponse.card_amount || 0) +
                (salesResponse.twint_amount || 0) || 0,
            manual: salesResponse.manual_amount || 0,
            total:
              (salesResponse.cash_amount || 0) +
              (salesResponse.card_amount || 0) +
              (salesResponse.twint_amount || 0) +
              (salesResponse.manual_amount || 0),
          };

          const tipsByMethod = tipsData.reduce(
            (acc, tip) => {
              const method = tip.payment_method.toLowerCase();
              acc[method] = (acc[method] || 0) + tip.total_tips;
              return acc;
            },
            {} as Record<string, number>,
          );
          console.log("tipsByMethod", tipsByMethod);

          recordedTips.value = {
            cash: tipsByMethod.cash || 0,
            card: (tipsByMethod.card || 0) + (tipsByMethod.twint || 0) || 0,
            manual: tipsByMethod.manual || 0,
            total:
              (tipsByMethod.cash || 0) +
              (tipsByMethod.card || 0) +
              (tipsByMethod.twint || 0) +
              (tipsByMethod.manual || 0),
          };
        } catch (e) {
          console.error("Failed to fetch data:", e);
          error.value = "Failed to load sales data. Please try again.";
        } finally {
          loading.value = false;
        }
      };

      onMounted(() => {
        fetchData();
      });

      const isValid = computed(() => {
        return (
          PAYMENT_METHODS.every((method) => {
            const diff = differences.value[method];
            return Number.isFinite(diff);
          }) && shiftId.value
        );
      });

      const handleSubmit = async () => {
        if (!isValid.value) {
          error.value = "Please check the differences. They seem too large.";
          return;
        }

        await props.onSubmit({
          starting_balances: startingBalances.value,
          closing_balances: closingBalances.value,
          differences: differences.value,
          notes: notes.value,
          shift_id: shiftId.value as string,
        });
        props.onClose();
      };

      const getMethodIcon = (method: string) => {
        switch (method) {
          case "cash":
            return "payments";
          case "card":
            return "credit_card";
          case "manual":
            return "receipt_long";
          default:
            return "payments";
        }
      };

      return () => (
        <LemmonModal
          title={t("reports.closing_reports.add_closing_report")}
          onClose={props.onClose}
          renderButtons={() => (
            <div class="modal-action px-4 py-3 border-t ">
              <button class="btn btn-ghost" onClick={() => props.onClose()}>
                {t("general.words.cancel")}
              </button>
              <button
                class="btn btn-primary"
                onClick={handleSubmit}
                disabled={props.isSavingReport || !isValid.value}
              >
                {t("reports.closing_reports.save_report_button")}
              </button>
            </div>
          )}
        >
          <div class="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
            {loading.value ? (
              <div class="flex justify-center items-center h-40">
                <span class="loading loading-spinner loading-lg"></span>
              </div>
            ) : (
              <>
                {userHasOpenTables.value && (
                  <div class="alert alert-warning mb-4">
                    <span class="material-symbols-outlined">warning</span>
                    <span>
                      You have open tables with active orders. Please close all
                      tables before filing a closing report.
                    </span>
                  </div>
                )}

                {error.value && (
                  <div class="alert alert-error mb-4">
                    <span class="material-symbols-outlined">error</span>
                    {error.value}
                  </div>
                )}

                <div class="space-y-6">
                  {PAYMENT_METHODS.map((method) => {
                    console.log(
                      "method",
                      method,
                      recordedSales.value,
                      recordedTips.value,
                    );
                    return (
                      <div
                        key={method}
                        class="flex flex-col lg:flex-row  border rounded my-4 px-4 py-4"
                      >
                        <h3 class="text-accent font-normal capitalize flex items-center  border-none lg:border-r py-4 lg:py-0 pr-4 w-24 flex justify-center items-center gap-2">
                          <span class="material-symbols-outlined">
                            {getMethodIcon(method)}
                          </span>
                          {method}
                        </h3>
                        <div class="flex-1 ">
                          <div class="grid grid-cols-3 space-x-2 mb-6">
                            <div class="text-center">
                              <div class="font-medium text-accent">
                                {formatPriceInLocale(
                                  recordedSales.value[method],
                                  true,
                                  2,
                                  auth.user.value?.restaurant.currency || "CHF",
                                )}
                              </div>
                              <div class="text-sm ">
                                {t("general.words.sales")}
                              </div>
                            </div>

                            <div class="text-center">
                              <div class="font-medium text-accent">
                                {formatPriceInLocale(
                                  recordedTips.value[method],
                                  true,
                                  2,
                                  auth.user.value?.restaurant.currency || "CHF",
                                )}
                              </div>
                              <div class="text-sm ">
                                {t("general.words.tips")}
                              </div>
                            </div>
                            <div class="text-center">
                              <div class="font-medium text-accent">
                                {formatPriceInLocale(
                                  recordedSales.value[method] +
                                    recordedTips.value[method],
                                  true,
                                  2,
                                  auth.user.value?.restaurant.currency || "CHF",
                                )}
                              </div>
                              <div class="text-sm ">
                                {t("reports.closing_reports.total_recorded")}
                              </div>
                            </div>
                          </div>

                          <div class="grid grid-cols-3">
                            {method === "cash" ? (
                              <>
                                <div class="text-center">
                                  <input
                                    type="number"
                                    class="input input-bordered w-12 lg:w-32 text-center"
                                    step="0.01"
                                    v-model={startingBalances.value[method]}
                                    placeholder="0.00"
                                  />
                                  <div class="text-sm ">
                                    {t("reports.closing_reports.starting")}
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div />
                            )}
                            <div class="text-center">
                              <input
                                type="number"
                                class="input input-bordered w-12 lg:w-32 text-center"
                                step="0.01"
                                v-model={closingBalances.value[method]}
                                placeholder="0.00"
                              />
                              <div class="text-sm ">
                                {t("reports.closing_reports.closing")}
                              </div>
                            </div>
                            <div class="mt-4 flex justify-center items-center">
                              <div
                                class={`text-center ${getDifferenceClass(
                                  differences.value[method],
                                )}`}
                              >
                                <div class="font-medium text-lg flex flex-col lg:flex-row items-center justify-center gap-2">
                                  {formatPriceInLocale(
                                    differences.value[method],
                                    true,
                                    2,
                                    auth.user.value?.restaurant.currency ||
                                      "CHF",
                                  )}
                                  <div
                                    id={differences.value[method].toFixed(2)}
                                    class="tooltip"
                                  >
                                    <span class="material-symbols-outlined">
                                      {getDifferenceIcon(
                                        differences.value[method],
                                      )}
                                    </span>
                                  </div>
                                </div>
                                <div class="text-sm ">
                                  {t("reports.closing_reports.difference")}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div class="form-control">
                  <label class="label">
                    <span class="label-text flex items-center gap-2">
                      <span class="material-symbols-outlined">notes</span>
                      {t("reports.closing_reports.notes")}
                    </span>
                  </label>
                  <textarea
                    class="textarea textarea-bordered h-24"
                    v-model={notes.value}
                    placeholder={t(
                      "reports.closing_reports.add_any_additional_notes_here...",
                    )}
                  />
                </div>
              </>
            )}
          </div>
        </LemmonModal>
      );
    },
  });
